/**
 * Sentry browser init for LexIDE. Imported first from main.tsx (before
 * createRoot) so the SDK is ready when React mounts.
 *
 * Contract documented in ai-docs/sentry-integration-guide.md. Do not
 * diverge from the scrubPII / IGNORE_ERRORS list without updating the
 * guide first.
 */

import * as Sentry from "@sentry/react";

import { reportErrorToActivity } from "./lib/activity/error-bridge";

const SERVICE_SLUG = "lexiun-ide";
const GIT_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? "unknown";
const LEXIUN_ENV = (import.meta.env.VITE_LEXIUN_ENV as string | undefined) ?? "development";
const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? "";

if (!DSN && LEXIUN_ENV === "production") {
  // eslint-disable-next-line no-console
  console.error(`[${SERVICE_SLUG}] VITE_SENTRY_DSN missing in production — error tracking disabled`);
}

// ── scrubPII (browser flavor) ─────────────────────────────────────────
// No request.headers / request.data shapes in the browser the way Node
// sees them, but breadcrumb data and extras can still carry secrets
// (fetch breadcrumbs include URLs with tokens, custom logs carry state).

const REDACTED = "[redacted]";
const JWT_REGEX = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const SENSITIVE_BODY_KEYS = new Set([
  "password",
  "token",
  "accessToken",
  "refreshToken",
  "secret",
  "apiKey",
  "api_key",
  "signingKey",
  "signing_key",
  "clerkSecretKey",
  "authorization",
]);
const MAX_STRING_LEN = 2048;

function redactKeysDeep(obj: unknown, keys: Set<string>): void {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    for (const item of obj) redactKeysDeep(item, keys);
    return;
  }
  const record = obj as Record<string, unknown>;
  for (const [k, v] of Object.entries(record)) {
    if (keys.has(k) || keys.has(k.toLowerCase())) {
      record[k] = REDACTED;
    } else if (typeof v === "string" && JWT_REGEX.test(v)) {
      record[k] = REDACTED;
    } else if (typeof v === "string" && v.length > MAX_STRING_LEN) {
      record[k] = `${v.slice(0, MAX_STRING_LEN)}…[truncated]`;
    } else if (v && typeof v === "object") {
      redactKeysDeep(v, keys);
    }
  }
}

function scrubPII(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.extra) redactKeysDeep(event.extra, SENSITIVE_BODY_KEYS);
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data) redactKeysDeep(crumb.data, SENSITIVE_BODY_KEYS);
    }
  }
  // Strip query-string secrets from the request URL (common pattern:
  // ?token=... in fetch breadcrumbs or session replay links).
  if (event.request?.url) {
    try {
      const u = new URL(event.request.url);
      for (const key of Array.from(u.searchParams.keys())) {
        if (SENSITIVE_BODY_KEYS.has(key.toLowerCase())) {
          u.searchParams.set(key, REDACTED);
        }
      }
      event.request.url = u.toString();
    } catch {
      // non-URL string — leave as-is
    }
  }
  // L7 — mirror the (already-scrubbed) error into the activity log.
  // beforeSend runs BEFORE React mounts, so reportErrorToActivity
  // buffers the event until <UserActivityProvider> registers a
  // listener and replays the buffer.
  try {
    const top = event.exception?.values?.[0];
    const message =
      top?.value ??
      event.message ??
      "<unknown error>";
    reportErrorToActivity({
      fingerprint: Array.isArray(event.fingerprint) ? event.fingerprint.join(":") : undefined,
      message: String(message),
    });
  } catch {
    // never let activity-bridge failures swallow the Sentry event
  }
  return event;
}

const IGNORE_ERRORS: Array<string | RegExp> = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  /^Non-Error promise rejection captured/,
  /NetworkError/,
  /AbortError/,
  /Failed to fetch/,
  "cancelled",
  /vid_mate_check/,
  /^ucbrowser/,
];

Sentry.init({
  dsn: DSN,
  release: `${SERVICE_SLUG}@${GIT_SHA}`,
  environment: LEXIUN_ENV,
  sendDefaultPii: false,
  tracesSampleRate: 0,
  debug: LEXIUN_ENV === "development",
  beforeSend: scrubPII,
  ignoreErrors: IGNORE_ERRORS,
});

Sentry.getGlobalScope().setTag("service", SERVICE_SLUG);
Sentry.getGlobalScope().setTag("runtime", "browser");

// Window-scoped dedupe: module-level flags are reset when Vite HMR
// re-evaluates this file (the whole point of HMR is a fresh module
// instance), so guard on `window` instead. One emit per full page load
// regardless of how many HMR cycles happen in between.
const smokeFlag = "__lexiunSentrySmokeEmitted";
if (
  import.meta.env.VITE_SENTRY_VERIFY === "1" &&
  DSN &&
  typeof window !== "undefined" &&
  !(window as unknown as Record<string, unknown>)[smokeFlag]
) {
  (window as unknown as Record<string, unknown>)[smokeFlag] = true;
  Sentry.captureMessage(`${SERVICE_SLUG} startup-check`, "info");
  // eslint-disable-next-line no-console
  console.log(`[${SERVICE_SLUG}] VITE_SENTRY_VERIFY=1 — sent startup-check event`);
}
